import '../styles/Experience.css';
import { Tooltip } from 'react-tooltip';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useApiCall } from '../hooks/useApiCall';
import { useAccount } from '../contexts/AccountContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import {
    RiArrowRightCircleFill,
    RiArrowLeftCircleFill,
    RiArrowLeftLine,
    RiHeartLine,
    RiHeartFill,
    RiHeart3Line,
    RiHeart3Fill,
    RiShoppingCartLine,
    RiSubtractLine,
    RiAddLine,
    RiCheckLine,
    RiErrorWarningLine,
    RiCloseLine,
    RiLoader4Line,
    RiDeleteBin6Line,
    RiArrowRightLine,
    RiTimerLine,
    RiTimerFlashLine
} from 'react-icons/ri';

// Pure function — no closures, no hooks — safe to call from any effect.
// Accepts dots array directly so it is decoupled from any module-level data ref.
const computeDotPositions = (dots, containerWidth, containerHeight) => {
    const videoAspectRatio = 1920 / 1080;
    const containerAspectRatio = containerWidth / containerHeight;
    return dots.map(dot => {
        let dotX, dotY;
        if (containerAspectRatio > videoAspectRatio) {
            const scale = containerWidth / 1920;
            const scaledVideoHeight = 1080 * scale;
            const cropTop = (scaledVideoHeight - containerHeight) / 2;
            dotX = (dot.x / 100) * containerWidth;
            dotY = (dot.y / 100) * scaledVideoHeight - cropTop;
        } else {
            const scale = containerHeight / 1080;
            const scaledVideoWidth = 1920 * scale;
            const cropLeft = (scaledVideoWidth - containerWidth) / 2;
            dotX = (dot.x / 100) * scaledVideoWidth - cropLeft;
            dotY = (dot.y / 100) * containerHeight;
        }
        return { x: dotX, y: dotY };
    });
};

function Experience() {

    const navigate = useNavigate();
    const location = useLocation();
    // Parse productId from path since Experience renders outside a <Route>
    const routeProductIdMatch = location.pathname.match(/^\/experience\/product\/(\d+)/);
    const routeProductId = routeProductIdMatch ? routeProductIdMatch[1] : undefined;
    const apiCall = useApiCall();
    const { account } = useAccount();
    const { refreshCounts, guestCart, guestWishlist, addToGuestCart, removeFromGuestCart, updateGuestCartQty, toggleGuestWishlist, isInGuestWishlist } = useCart();
    const { showSuccess, showError } = useToast();
    const videoRef = useRef(null);
    const overlayTimeoutRef = useRef(null);
    const autoAdvanceTimerRef = useRef(null);
    const travelStartRef = useRef(0);
    const travelEndRef = useRef(0);

    const nextTimestampRef = useRef(null);
    const prevTimestampRef = useRef(null);
    const [ sections, setSections ] = useState([]);
    const [ sectionsLoading, setSectionsLoading ] = useState(true);
    const [ sectionsError, setSectionsError ] = useState('');
    const [ currentSectionIndex, setCurrentSectionIndex ] = useState(0);
    const [ currentTimestampIndex, setCurrentTimestampIndex ] = useState(0);
    const [ videoInitialized, setVideoInitialized ] = useState(false);

    const currentSection = sections[currentSectionIndex] ?? null;
    const VIDEO_DATA = currentSection?.video ?? null;
    const CHAPTER_LABELS = currentSection?.chapterLabels ?? [];
    const hasShowroomContent = Boolean(VIDEO_DATA?.uri && Array.isArray(VIDEO_DATA?.timestamps) && VIDEO_DATA.timestamps.length > 0);
    const [ showDots, setShowDots ] = useState(false);
    const [ showOverlay, setShowOverlay ] = useState(true);
    const [ overlayFading, setOverlayFading ] = useState(false);
    const [ isAtTargetTimestamp, setIsAtTargetTimestamp ] = useState(false);
    const [ travelSegmentIndex, setTravelSegmentIndex ] = useState(null);
    const progressBarRef = useRef(null);
    const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(true);

    const [selectedDot, setSelectedDot] = useState(null);
    const [cardPosition, setCardPosition] = useState({ x: 0, y: 0, side: 'right' });
    const [wishlist, setWishlist] = useState([]);
    const [showChapterName, setShowChapterName] = useState(false);
    // { chapterIndex, positions[] } — positions are only valid for their chapter
    const [dotPixelPositions, setDotPixelPositions] = useState({ chapterIndex: -1, positions: [] });

    // Product drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerProduct, setDrawerProduct] = useState(null);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [isBuffering, setIsBuffering] = useState(true);
    const bufferingTimerRef = useRef(null);
    const [drawerImageIndex, setDrawerImageIndex] = useState(0);
    const [drawerQuantity, setDrawerQuantity] = useState(1);
    const [drawerModifiers, setDrawerModifiers] = useState({});
    const [drawerWishlisted, setDrawerWishlisted] = useState(false);
    const [drawerAddingToCart, setDrawerAddingToCart] = useState(false);
    const [drawerAddedToCart, setDrawerAddedToCart] = useState(false);
    const [drawerTogglingWishlist, setDrawerTogglingWishlist] = useState(false);
    const [experienceCartOpen, setExperienceCartOpen] = useState(false);
    const [experienceCartLoading, setExperienceCartLoading] = useState(false);
    const [experienceCartItems, setExperienceCartItems] = useState([]);
    const [experienceCartUpdating, setExperienceCartUpdating] = useState({});

    // O(1) wishlist lookup — rebuilds only when wishlist changes
    const wishlistSet = useMemo(() => new Set(wishlist.map(w => w.title)), [wishlist]);
    const wishlistProductIds = useMemo(() => new Set(wishlist.filter(w => w.productId).map(w => w.productId)), [wishlist]);
    const isWishlisted = useCallback((item) => {
        if (item.productId) return wishlistProductIds.has(item.productId);
        return wishlistSet.has(item.title);
    }, [wishlistSet, wishlistProductIds]);

    // Sync guestWishlist → local wishlist state when no account
    useEffect(() => {
        if (account?.token) return;
        setWishlist(guestWishlist.map(w => ({ productId: w.product_id, title: w.title, image: w.image })));
    }, [guestWishlist, account]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync guestCart → in-experience cart panel when no account
    useEffect(() => {
        if (account?.token) return;
        setExperienceCartItems(guestCart.map(item => ({
            cart_id: `guest_${item.product_id}`,
            product_id: item.product_id,
            qty: item.qty,
            name: item.name,
            primary_image: item.image,
            price: item.price,
            price_sale: item.price_sale,
            price_curr: item.price_curr,
        })));
    }, [guestCart, account]); // eslint-disable-line react-hooks/exhaustive-deps


    // Clear travel indicator when arriving at a chapter
    useEffect(() => {
        if (isAtTargetTimestamp) {
            setTravelSegmentIndex(null);
            if (progressBarRef.current) progressBarRef.current.style.width = '0%';
        }
    }, [isAtTargetTimestamp]);

    // RAF loop: update travel progress while video plays between chapters
    // Drives the progress bar via a DOM ref — no React state, no re-renders, perfectly smooth.
    useEffect(() => {
        if (isAtTargetTimestamp || travelSegmentIndex === null) return;
        let rafId;
        const update = () => {
            const ct = videoRef.current?.currentTime || 0;
            const duration = travelEndRef.current - travelStartRef.current;
            if (duration > 0 && progressBarRef.current) {
                const pct = Math.min(100, Math.max(0, ((ct - travelStartRef.current) / duration) * 100));
                progressBarRef.current.style.width = pct + '%';
            }
            rafId = requestAnimationFrame(update);
        };
        rafId = requestAnimationFrame(update);
        return () => cancelAnimationFrame(rafId);
    }, [isAtTargetTimestamp, travelSegmentIndex]);

    // Compute dot pixel positions AFTER layout is committed, so DOM reads are accurate
    useEffect(() => {
        if (!hasShowroomContent) {
            setDotPixelPositions({ chapterIndex: -1, positions: [] });
            return;
        }
        if (!showDots) {
            setDotPixelPositions({ chapterIndex: -1, positions: [] });
            return;
        }
        const capturedChapter = currentTimestampIndex;
        const dots = VIDEO_DATA.timestamps[capturedChapter]?.dots || [];
        const raf = requestAnimationFrame(() => {
            const containerWidth = videoRef.current?.parentElement?.clientWidth || window.innerWidth;
            const containerHeight = videoRef.current?.parentElement?.clientHeight || window.innerHeight;
            setDotPixelPositions({
                chapterIndex: capturedChapter,
                positions: computeDotPositions(dots, containerWidth, containerHeight)
            });
        });
        return () => cancelAnimationFrame(raf);
    }, [showDots, currentTimestampIndex, currentSectionIndex, hasShowroomContent]); // eslint-disable-line react-hooks/exhaustive-deps

    // Recompute dot positions on window resize
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        const handleResize = () => {
            const dots = VIDEO_DATA.timestamps[currentTimestampIndex]?.dots || [];
            const containerWidth = videoRef.current?.parentElement?.clientWidth || window.innerWidth;
            const containerHeight = videoRef.current?.parentElement?.clientHeight || window.innerHeight;
            requestAnimationFrame(() => {
                setDotPixelPositions({
                    chapterIndex: currentTimestampIndex,
                    positions: computeDotPositions(dots, containerWidth, containerHeight)
                });
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [currentTimestampIndex, currentSectionIndex, hasShowroomContent]); // eslint-disable-line react-hooks/exhaustive-deps

    // Video navigation functions
    const nextTimestamp = () => {
        if (!hasShowroomContent) return;
        setSelectedDot(null);
        if (videoRef.current && isAtTargetTimestamp && videoRef.current.paused) {
            if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
                overlayTimeoutRef.current = null;
            }
            setShowDots(false);
            setShowOverlay(true);
            setOverlayFading(false);
            setIsAtTargetTimestamp(false);
            if (currentTimestampIndex < VIDEO_DATA.timestamps.length - 1) {
                const newIndex = currentTimestampIndex + 1;
                setTravelSegmentIndex(currentTimestampIndex); // travelling segment between ch i and i+1
                setCurrentTimestampIndex(newIndex);
                const startTime = videoRef.current.currentTime; // resume from exact pause point — no jump
                travelStartRef.current = startTime;
                travelEndRef.current = VIDEO_DATA.timestamps[newIndex].seconds;
                videoRef.current.play().catch(() => {});
            } else {
                videoRef.current.play().catch(() => {});
            }
        } else {
            const currentTime = videoRef.current?.currentTime || 0;
            const currentTimestamp = VIDEO_DATA.timestamps[currentTimestampIndex];
            if (currentTime < currentTimestamp.seconds) {
                videoRef.current.currentTime = currentTimestamp.seconds;
                videoRef.current.pause();
                setIsAtTargetTimestamp(true);
                setShowDots(true);
            } else if (currentTimestampIndex < VIDEO_DATA.timestamps.length - 1) {
                const newIndex = currentTimestampIndex + 1;
                videoRef.current.currentTime = VIDEO_DATA.timestamps[newIndex].seconds;
                videoRef.current.pause();
                setCurrentTimestampIndex(newIndex);
                setIsAtTargetTimestamp(true);
                setShowDots(true);
            } else {
                const ts = VIDEO_DATA.timestamps[currentTimestampIndex];
                if (Math.abs((videoRef.current?.currentTime || 0) - ts.seconds) < 0.5) {
                    videoRef.current.currentTime = ts.seconds + 1;
                }
                videoRef.current.play().catch(() => {});
            }
        }
    };

    const prevTimestamp = () => {
        if (!hasShowroomContent) return;
        setSelectedDot(null);
        if (videoRef.current && isAtTargetTimestamp && videoRef.current.paused) {
            if (currentTimestampIndex === 0) return; // already at the start, do nothing
            if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
                overlayTimeoutRef.current = null;
            }
            setShowDots(false);
            setShowOverlay(true);
            setOverlayFading(false);
            setIsAtTargetTimestamp(false);
            setTravelSegmentIndex(currentTimestampIndex - 1); // travelling segment between ch i-1 and i
            const startTime = VIDEO_DATA.timestamps[currentTimestampIndex - 1].seconds + 2;
            travelStartRef.current = startTime;
            travelEndRef.current = VIDEO_DATA.timestamps[currentTimestampIndex].seconds;
            videoRef.current.currentTime = startTime;
            videoRef.current.play().catch(() => {});
        } else {
            if (currentTimestampIndex > 0) {
                const prevIndex = currentTimestampIndex - 1;
                videoRef.current.currentTime = VIDEO_DATA.timestamps[prevIndex].seconds;
                videoRef.current.pause();
                setCurrentTimestampIndex(prevIndex);
                setIsAtTargetTimestamp(true);
                setShowDots(true);
            }
            // at chapter 0 with video playing — do nothing
        }
    };

    
    // Video event handlers
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        const videoElement = videoRef.current;
        if (!videoElement) return;

        // RAF loop: monitors currentTime tightly (~16ms) and stops the video
        // exactly at the target chapter — avoids the timeupdate overshoot/snap-back flash.
        let stopRafId = null;
        const LOOKAHEAD = 1 / 30; // one frame early so the snap is forward, not backward

        const startStopWatcher = () => {
            if (stopRafId) return;
            const watch = () => {
                if (videoElement.paused || isAtTargetTimestamp) { stopRafId = null; return; }
                const ct = videoElement.currentTime;
                const target = VIDEO_DATA.timestamps[currentTimestampIndex].seconds;
                if (ct >= target - LOOKAHEAD) {
                    videoElement.pause();
                    videoElement.currentTime = target;
                    setShowDots(true);
                    setIsAtTargetTimestamp(true);
                    setOverlayFading(true);
                    if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
                    overlayTimeoutRef.current = setTimeout(() => {
                        setShowOverlay(false);
                        setOverlayFading(false);
                    }, 1500);
                    stopRafId = null;
                    return;
                }
                stopRafId = requestAnimationFrame(watch);
            };
            stopRafId = requestAnimationFrame(watch);
        };

        const handlePlay = () => {
            if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
                overlayTimeoutRef.current = null;
            }
            setShowDots(false);
            setIsAtTargetTimestamp(false);
            setShowOverlay(true);
            setOverlayFading(false);
            startStopWatcher();
        };

        const handlePause = () => {
            if (stopRafId) { cancelAnimationFrame(stopRafId); stopRafId = null; }
            // Ignore pause events fired during buffering — not a real chapter arrival
            if (videoElement.readyState < 3) return;
            const currentTime = videoElement.currentTime;
            const currentTimestamp = VIDEO_DATA.timestamps[currentTimestampIndex];
            if (Math.abs(currentTime - currentTimestamp.seconds) < 0.5) {
                setShowDots(true);
                setIsAtTargetTimestamp(true);
                setOverlayFading(true);
                if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
                overlayTimeoutRef.current = setTimeout(() => {
                    setShowOverlay(false);
                    setOverlayFading(false);
                }, 1500);
            }
        };

        const handleLoadedData = () => {
            if (!videoInitialized) {
                videoElement.currentTime = 0;
                setShowOverlay(true);
                setOverlayFading(false);
                videoElement.play().catch(() => {});
                setVideoInitialized(true);
            }
        };

        // If video is already playing when this effect re-runs, restart the watcher
        if (!videoElement.paused && !isAtTargetTimestamp) startStopWatcher();

        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);
        videoElement.addEventListener('loadeddata', handleLoadedData);

        return () => {
            if (stopRafId) { cancelAnimationFrame(stopRafId); stopRafId = null; }
            // Clear timeout on cleanup
            if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
            }
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
            videoElement.removeEventListener('loadeddata', handleLoadedData);
        };
    }, [currentTimestampIndex, videoInitialized, isAtTargetTimestamp, hasShowroomContent, VIDEO_DATA?.timestamps]);

    // Pause video when navigating away (component unmounts)
    useEffect(() => {
        return () => {
            if (videoRef.current && !videoRef.current.paused) {
                videoRef.current.pause();
            }
        };
    }, []);

    // When section switches, the new src is committed to the DOM — call .load() so
    // the browser acknowledges it and fires loadeddata → handleLoadedData → play()
    useEffect(() => {
        if (!hasShowroomContent || !videoRef.current) return;
        videoRef.current.load();
    }, [currentSectionIndex, hasShowroomContent]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load sections from API only.
    useEffect(() => {
        let cancelled = false;
        apiCall('showroom/sections', 'GET')
            .then(res => {
                if (cancelled) return;
                if (res?.success && Array.isArray(res.data?.sections) && res.data.sections.length > 0) {
                    setSections(res.data.sections);
                    setSectionsError('');
                } else {
                    setSections([]);
                    setSectionsError(res?.response || 'No showroom sections are available yet.');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSections([]);
                    setSectionsError('Unable to load the showroom right now.');
                }
            })
            .finally(() => { if (!cancelled) setSectionsLoading(false); });
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize video on mount
    useEffect(() => {
        if (hasShowroomContent && videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play().catch(() => {});
        }
    }, [hasShowroomContent]);

    const handleDotClick = (dot, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const cardWidth = 450;
        const dotX = rect.left + rect.width / 2;
        const y = window.innerHeight / 2;
        
        // Simple left/right logic based on available space
        let side = 'right';
        let x = dotX + 50; // Default to right side
        
        // If right side would go off screen, use left side
        if (dotX + 50 + cardWidth > viewportWidth - 20) {
            side = 'left';
            x = dotX - 50;
        }
        
        // Set position first, then show card
        setCardPosition({ x, y, side });
        setSelectedDot(dot);
    };

    const closePopup = () => {
        setSelectedDot(null);
    };

    const jumpToTimestamp = (index) => {
        if (!hasShowroomContent || !videoRef.current) return;
        if (!videoRef.current) return;
        videoRef.current.currentTime = VIDEO_DATA.timestamps[index].seconds;
        videoRef.current.pause();
        setCurrentTimestampIndex(index);
        setIsAtTargetTimestamp(true);
        setShowDots(true);
        setSelectedDot(null);
    };

    const switchSection = (index) => {
        if (index === currentSectionIndex) return;
        // Reset all video state for the new section
        setCurrentSectionIndex(index);
        setCurrentTimestampIndex(0);
        setVideoInitialized(false);
        setShowDots(false);
        setSelectedDot(null);
        setIsAtTargetTimestamp(false);
        setShowOverlay(true);
        setOverlayFading(false);
        if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
        if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
        // Video src will update on next render via VIDEO_DATA; restart in the init effect
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    };

    const toggleWishlist = async (item) => {
        if (!account?.token) {
            if (item.productId) {
                const isIn = wishlistProductIds.has(item.productId);
                toggleGuestWishlist({ product_id: item.productId, name: item.title, image: item.image || null, price: String(item.price || 0), price_curr: item.price_curr || 'SRD' });
                setWishlist(prev => isIn
                    ? prev.filter(w => w.productId !== item.productId)
                    : [...prev, item]);
            } else {
                setWishlist(prev => {
                    const isIn = prev.some(w => w.title === item.title);
                    return isIn ? prev.filter(w => w.title !== item.title) : [...prev, item];
                });
            }
            return;
        }
        if (item.productId) {
            const isIn = wishlistProductIds.has(item.productId);
            // Optimistic update
            setWishlist(prev => isIn
                ? prev.filter(w => w.productId !== item.productId)
                : [...prev, item]);
            const endpoint = isIn ? 'account/wishlist/remove' : 'account/wishlist/add';
            try {
                const res = await apiCall(endpoint, 'POST', { product_id: item.productId }, account);
                if (res?.success) {
                    refreshCounts();
                } else {
                    // Revert
                    setWishlist(prev => isIn ? [...prev, item] : prev.filter(w => w.productId !== item.productId));
                    showError(res?.response || 'Failed to update wishlist');
                }
            } catch {
                setWishlist(prev => isIn ? [...prev, item] : prev.filter(w => w.productId !== item.productId));
                showError('Failed to update wishlist');
            }
        } else {
            // Local-only toggle for static items without a real product link
            setWishlist(prev => {
                const isIn = prev.some(w => w.title === item.title);
                return isIn ? prev.filter(w => w.title !== item.title) : [...prev, item];
            });
        }
    };

    // ── Product Drawer ──────────────────────────────────────────────────────────

    const openProductDrawer = (productId, updateHistory = true) => {
        setDrawerOpen(true);
        setDrawerProduct(null);
        setDrawerLoading(true);
        setDrawerImageIndex(0);
        setDrawerQuantity(1);
        setDrawerModifiers({});
        setDrawerWishlisted(false);
        setDrawerAddingToCart(false);
        setDrawerAddedToCart(false);
        setDrawerTogglingWishlist(false);
        setExperienceCartUpdating({});
        if (updateHistory) {
            navigate(`/experience/product/${productId}`, { replace: true });
        }

        apiCall('product/get', 'POST', { product_id: parseInt(productId) }).then(res => {
            if (res?.success && res.data?.product) {
                setDrawerProduct(res.data.product);
                // Check wishlist status
                if (account?.token) {
                    apiCall('account/wishlist/check', 'POST', { product_id: parseInt(productId) }, account).then(r => {
                        if (r?.success) setDrawerWishlisted(r.data.in_wishlist);
                    }).catch(() => {});
                } else {
                    setDrawerWishlisted(isInGuestWishlist(parseInt(productId)));
                }
            }
            setDrawerLoading(false);
        }).catch(() => setDrawerLoading(false));
    };

    const closeProductDrawer = () => {
        setDrawerOpen(false);
        setTimeout(() => setDrawerProduct(null), 350);
        navigate('/experience', { replace: true });
    };

    // Sync drawer open/closed state with URL path changes
    useEffect(() => {
        if (routeProductId) {
            // URL has a product ID — open drawer if not already showing this product
            if (!drawerOpen || drawerProduct?.id !== parseInt(routeProductId)) {
                openProductDrawer(parseInt(routeProductId), false);
            }
        } else if (drawerOpen && location.pathname === '/experience') {
            // Back-navigated to /experience — close drawer
            setDrawerOpen(false);
            setTimeout(() => setDrawerProduct(null), 350);
        }
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchDrawerCart = useCallback(async () => {
        if (!account?.token) return; // guestCart effect manages experience cart for guests

        setExperienceCartLoading(true);
        try {
            const response = await apiCall('account/cart/get', 'POST', {});
            if (response?.success) {
                setExperienceCartItems(response.data?.cart || []);
            } else {
                showError(response?.response || response?.message || 'Failed to load cart');
            }
        } catch {
            showError('Failed to load cart');
        } finally {
            setExperienceCartLoading(false);
        }
    }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (account?.token) {
            fetchDrawerCart();
            return;
        }
        // guestCart sync effect handles populating experienceCartItems for guests.
        // Only close the cart panel if the user is not signed in.
        setExperienceCartOpen(false);
    }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

    const getDrawerCartItemPrice = (item) => {
        const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
        return hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
    };

    const getDrawerCartItemTotal = (item) => {
        const qty = parseInt(item.qty, 10) || 0;
        // Modifier amount replaces base price (versions/variants)
        let unitPrice = getDrawerCartItemPrice(item);
        if (item.options) {
            try {
                const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                if (options?.modifiers && Array.isArray(options.modifiers)) {
                    const priceModifier = options.modifiers.find(m => m.option?.amount && parseFloat(m.option.amount) > 0);
                    if (priceModifier) unitPrice = parseFloat(priceModifier.option.amount);
                }
            } catch {
                return unitPrice * qty;
            }
        }
        return unitPrice * qty;
    };

    const getDrawerCartCurrency = () => experienceCartItems[0]?.price_curr || drawerProduct?.price?.curr || '$';

    const getDrawerCartTotal = () => experienceCartItems.reduce((sum, item) => sum + getDrawerCartItemTotal(item), 0);

    const toggleExperienceCart = async () => {
        const nextOpen = !experienceCartOpen;
        setExperienceCartOpen(nextOpen);
        if (nextOpen) {
            await fetchDrawerCart();
        }
    };

    const openExperienceCart = async () => {
        if (drawerOpen) {
            setDrawerOpen(false);
            setTimeout(() => setDrawerProduct(null), 350);
            navigate('/experience', { replace: true });
        }
        setExperienceCartOpen(true);
        await fetchDrawerCart();
    };

    const updateDrawerCartQuantity = async (cartId, nextQty) => {
        if (!account?.token) {
            if (String(cartId).startsWith('guest_')) {
                updateGuestCartQty(parseInt(String(cartId).replace('guest_', '')), nextQty);
            }
            return;
        }
        if (nextQty < 1) return;
        setExperienceCartUpdating(prev => ({ ...prev, [cartId]: true }));
        try {
            const response = await apiCall('account/cart/update', 'POST', { cart_id: cartId, qty: nextQty }, account, null);
            if (response?.success) {
                setExperienceCartItems(prev => prev.map(item => item.cart_id === cartId ? { ...item, qty: nextQty } : item));
                refreshCounts();
            } else {
                showError(response?.response || response?.message || 'Failed to update cart');
            }
        } catch {
            showError('Failed to update cart');
        } finally {
            setExperienceCartUpdating(prev => ({ ...prev, [cartId]: false }));
        }
    };

    const removeDrawerCartItem = async (cartId) => {
        if (!account?.token) {
            if (String(cartId).startsWith('guest_')) {
                removeFromGuestCart(parseInt(String(cartId).replace('guest_', '')));
            }
            return;
        }
        setExperienceCartUpdating(prev => ({ ...prev, [cartId]: true }));
        try {
            const response = await apiCall('account/cart/remove', 'POST', { cart_id: cartId }, account, null);
            if (response?.success) {
                setExperienceCartItems(prev => prev.filter(item => item.cart_id !== cartId));
                refreshCounts();
            } else {
                showError(response?.response || response?.message || 'Failed to remove item');
            }
        } catch {
            showError('Failed to remove item');
        } finally {
            setExperienceCartUpdating(prev => ({ ...prev, [cartId]: false }));
        }
    };

    const getDrawerTransformed = () => {
        if (!drawerProduct) return null;
        const p = drawerProduct;
        return {
            id: p.id,
            name: p.name,
            shortDescription: p.description ? p.description.substring(0, 120) + '...' : '',
            description: p.description || '',
            comingSoon: p.coming_soon || false,
            price: {
                curr: p.price?.curr || '$',
                amount: p.sale_price && p.sale_price > 0 ? p.sale_price : p.price?.amount || 0,
                originalAmount: p.sale_price && p.sale_price > 0 ? p.price?.amount : null,
                discount: p.sale_price && p.sale_price > 0 ?
                    Math.round(((p.price?.amount - p.sale_price) / p.price?.amount) * 100) : 0
            },
            images: p.images && p.images.length > 0 ? p.images : [{ id: 1, url: '/data/images/placeholder.jpg', alt: p.name }],
            modifiers: p.modifiers && typeof p.modifiers === 'object' ?
                Object.entries(p.modifiers).map(([key, value]) => ({
                    id: key.toLowerCase().replace(/\s+/g, '_'),
                    name: key,
                    required: false,
                    options: Array.isArray(value) ? value.map(opt => ({
                        id: `${key.toLowerCase()}_${opt.id}`,
                        name: opt.name,
                        description: opt.description || '',
                        amount: opt.amount || 0,
                        inStock: true
                    })) : []
                })) : [],
            features: p.features ? p.features.split('\n').filter(f => f.trim()) : [],
            inStock: p.in_stock || false,
            stockCount: p.stock || 0,
            brand: p.brand || '',
        };
    };

    const calculateDrawerTotal = (tp) => {
        if (!tp) return 0;
        // Modifier amount replaces base price (versions/variants)
        const modifierAmounts = Object.values(drawerModifiers).filter(opt => opt?.amount > 0);
        const unitPrice = modifierAmounts.length > 0 ? modifierAmounts[0].amount : tp.price.amount;
        return unitPrice * drawerQuantity;
    };

    const handleDrawerAddToCart = async (tp) => {
        if (tp.comingSoon) { showError('This product is coming soon'); return; }
        if (!tp.inStock) { showError('This product is currently out of stock'); return; }

        const requiredMissing = tp.modifiers.filter(m => m.required && !drawerModifiers[m.id]);
        if (requiredMissing.length > 0) { showError(`Please select: ${requiredMissing.map(m => m.name).join(', ')}`); return; }

        const mods = Object.entries(drawerModifiers).map(([mid, opt]) => {
            const mod = tp.modifiers.find(m => m.id === mid);
            return { name: mod.name, option: { item: opt.name, amount: opt.amount || 0 } };
        });
        const options = mods.length > 0 ? JSON.stringify({ modifiers: mods }) : null;

        if (!account?.token) {
            addToGuestCart({
                product_id: tp.id,
                qty: drawerQuantity,
                options,
                name: tp.name,
                image: tp.images?.[0]?.url || null,
                price: String(tp.price?.amount || 0),
                price_sale: null,
                price_curr: tp.price?.curr || 'SRD',
            });
            setDrawerAddedToCart(true);
            showSuccess('Added to cart');
            refreshCounts();
            return;
        }

        setDrawerAddingToCart(true);
        try {
            const res = await apiCall('account/cart/add', 'POST', { product_id: tp.id, qty: drawerQuantity, options }, account);
            if (res?.success) {
                setDrawerAddedToCart(true);
                refreshCounts();
                fetchDrawerCart();
            } else {
                showError(res?.response || 'Failed to add product to cart');
            }
        } catch { showError('Failed to add product to cart'); }
        finally { setDrawerAddingToCart(false); }
    };

    const handleDrawerWishlistToggle = async (tp) => {
        if (!account?.token) {
            toggleGuestWishlist({ product_id: tp.id, name: tp.name, image: tp.images?.[0]?.url || null, price: String(tp.price?.amount || 0), price_curr: tp.price?.curr || 'SRD' });
            setDrawerWishlisted(v => !v);
            showSuccess(drawerWishlisted ? 'Removed from wishlist' : 'Added to wishlist');
            refreshCounts();
            return;
        }
        setDrawerTogglingWishlist(true);
        try {
            const endpoint = drawerWishlisted ? 'account/wishlist/remove' : 'account/wishlist/add';
            const body = { product_id: tp.id };
            if (!drawerWishlisted) {
                const mods = Object.entries(drawerModifiers).map(([mid, opt]) => {
                    const mod = tp.modifiers.find(m => m.id === mid);
                    return { name: mod.name, option: { item: opt.name, amount: opt.amount || 0 } };
                });
                if (mods.length > 0) body.options = JSON.stringify({ modifiers: mods });
            }
            const res = await apiCall(endpoint, 'POST', body, account);
            if (res?.success) {
                setDrawerWishlisted(!drawerWishlisted);
                showSuccess(drawerWishlisted ? 'Removed from wishlist' : 'Added to wishlist', 3000, () => navigate('/wishlist'));
                refreshCounts();
            } else { showError(res?.response || 'Failed to update wishlist'); }
        } catch { showError('Failed to update wishlist'); }
        finally { setDrawerTogglingWishlist(false); }
    };

    // Sync refs in render body — cheaper than two effects that run every render
    nextTimestampRef.current = nextTimestamp;
    prevTimestampRef.current = prevTimestamp;

    // Keyboard navigation
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        const handleKey = (e) => {
            if (e.key === 'ArrowRight') nextTimestampRef.current?.();
            else if (e.key === 'ArrowLeft') prevTimestampRef.current?.();
            else if (e.key === 'Escape') setSelectedDot(null);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [hasShowroomContent]);

    // Auto-advance: after 15s paused at a chapter with no card open, play to next
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        if (!autoAdvanceEnabled || !isAtTargetTimestamp || selectedDot || currentTimestampIndex >= VIDEO_DATA.timestamps.length - 1 || isBuffering) {
            return;
        }
        const timer = setTimeout(() => {
            if (!videoRef.current) return;
            const newIndex = currentTimestampIndex + 1;
            const startT = VIDEO_DATA.timestamps[currentTimestampIndex].seconds + 2;
            travelStartRef.current = startT;
            travelEndRef.current = VIDEO_DATA.timestamps[newIndex].seconds;
            videoRef.current.currentTime = startT;
            setCurrentTimestampIndex(newIndex);
            setTravelSegmentIndex(currentTimestampIndex);
            setShowDots(false);
            setShowOverlay(true);
            setOverlayFading(false);
            setIsAtTargetTimestamp(false);
            setSelectedDot(null);
            videoRef.current.play().catch(() => {});
        }, 15 * 1000);
        autoAdvanceTimerRef.current = timer;
        return () => clearTimeout(timer);
    }, [isAtTargetTimestamp, selectedDot, currentTimestampIndex, hasShowroomContent, autoAdvanceEnabled, isBuffering, VIDEO_DATA?.timestamps]);

    // Chapter name toast: show for 2.5s each time we arrive at a new timestamp
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        if (!isAtTargetTimestamp) return;
        setShowChapterName(true);
        const t = setTimeout(() => setShowChapterName(false), 4000);
        return () => clearTimeout(t);
    }, [isAtTargetTimestamp, currentTimestampIndex, hasShowroomContent]);

    const renderVideoBanner = () => {
        const currentTimestamp = VIDEO_DATA.timestamps[currentTimestampIndex];
        
        const handleContainerClick = (e) => {
            // Ignore clicks that originated from a dot or card
            if (e.target.closest('.Dot') || e.target.closest('.FloatingCard')) return;
            // Only act while video is playing — jump to next checkpoint
            if (videoRef.current && !videoRef.current.paused) {
                nextTimestampRef.current?.();
            }
        };

        return (
            <div className="VideoBannerContainer" onClick={handleContainerClick}>
                <video 
                    ref={videoRef}
                    src={VIDEO_DATA.uri}
                    muted
                    className="VideoBannerVideo"
                    onLoadStart={() => {
                        if (bufferingTimerRef.current) clearTimeout(bufferingTimerRef.current);
                        bufferingTimerRef.current = setTimeout(() => setIsBuffering(true), 400);
                    }}
                    onWaiting={() => {
                        if (bufferingTimerRef.current) clearTimeout(bufferingTimerRef.current);
                        bufferingTimerRef.current = setTimeout(() => setIsBuffering(true), 400);
                    }}
                    onPlaying={() => {
                        if (bufferingTimerRef.current) { clearTimeout(bufferingTimerRef.current); bufferingTimerRef.current = null; }
                        setIsBuffering(false);
                    }}
                    onCanPlay={() => {
                        if (bufferingTimerRef.current) { clearTimeout(bufferingTimerRef.current); bufferingTimerRef.current = null; }
                        setIsBuffering(false);
                    }}
                />
                {isBuffering && (
                    <div className="VideoBufferingIndicator">
                        <div className="VideoBufferingSpinner"></div>
                    </div>
                )}
                {/* Video overlay with center mask */}
                {showOverlay && (
                    <div className={`VideoOverlay ${overlayFading ? 'fading' : ''}`}></div>
                )}
                {/* Container that matches the actual video dimensions */}
                <div className="VideoDotsContainer">
                    {showDots && dotPixelPositions.chapterIndex === currentTimestampIndex && currentTimestamp?.dots && currentTimestamp.dots.map((dot, dotIndex) => {
                        const isSelected = selectedDot === dot;
                        const pos = dotPixelPositions.positions[dotIndex];
                        if (!pos) return null;

                        return (
                            <div 
                                className={`Dot VideoDot ${isSelected ? 'selected' : ''} visible`}
                                key={dotIndex} 
                                style={{
                                    left: pos.x + 'px',
                                    top: pos.y + 'px'
                                }}
                                onClick={(e) => handleDotClick(dot, e)}
                            >
                                <div className="DotIndicator"><span className="DotPlus">+</span></div>
                            </div>
                        )
                    })}
                </div>
                {selectedDot && (
                    <div 
                        className={`FloatingCard ${cardPosition.side}`}
                        style={{
                            left: cardPosition.x + 'px',
                            top: cardPosition.y + 'px'
                        }}
                    >
                        {/* Connecting line from dot to card */}
                        <div className={`ConnectingLine ${cardPosition.side}`}></div>
                        <button className="CloseButton" onClick={closePopup}>×</button>
                        <div className="CardContent">
                            <div className="CardInfo">
                                <div className="CardHeader">
                                    <h2>{selectedDot.item.title}</h2>
                                    <span className="CardSubtitle">{selectedDot.item.subtitle}</span>
                                    <div className="PriceSection">
                                        <span className="CurrentPrice">{selectedDot.item.price}</span>
                                        {selectedDot.item.originalPrice && (
                                            <span className="OriginalPrice">{selectedDot.item.originalPrice}</span>
                                        )}
                                    </div>
                                </div>
                                <p className="Description">{selectedDot.item.text}</p>
                                <div className="Features">
                                    <h4>Key Features:</h4>
                                    <ul>
                                        {selectedDot.item.features.map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="CardActions">
                                    <div className="StockStatus">
                                        <span className={`Stock ${selectedDot.item.inStock ? 'inStock' : 'outOfStock'}`}>
                                            {selectedDot.item.inStock ? '✓ In Stock' : '⏳ Pre-Order'}
                                        </span>
                                    </div>
                                        <div className="CardActionsRow">
                                        <button
                                            className={`WishlistBtn ${isWishlisted(selectedDot.item) ? 'active' : ''}`}
                                            onClick={() => toggleWishlist(selectedDot.item)}
                                            title={isWishlisted(selectedDot.item) ? 'Remove from wishlist' : 'Save to wishlist'}
                                        >
                                            {isWishlisted(selectedDot.item)
                                                ? <RiHeartFill size={16} />
                                                : <RiHeartLine size={16} />}
                                        </button>
                                        {selectedDot.item.productId ? (
                                            <button
                                                className="ViewProductBtn"
                                                onClick={() => openProductDrawer(selectedDot.item.productId)}
                                            >
                                                View Product
                                            </button>
                                        ) : (
                                            <button className="AddToCartBtn">
                                                {selectedDot.item.inStock ? 'Order' : 'Pre-Order Now'}
                                            </button>
                                        )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                {/* Chapter name toast */}
                {showChapterName && (
                    <div className="ChapterNameToast">
                        <span className="ChapterNameRoom">Room {currentTimestampIndex + 1} of {VIDEO_DATA.timestamps.length}</span>
                        <span className="ChapterNameLabel">{CHAPTER_LABELS[currentTimestampIndex]}</span>
                    </div>
                )}

                {/* Auto-advance progress bar — only once video is loaded and playing */}
            </div>
        );
    };

    const getMainView = () => {
        if (sectionsLoading) {
            return (
                <div className="ExperienceStateScreen">
                    <div className="ExperienceStateCard">
                        <RiLoader4Line className="ExperienceStateSpinner" size={26} />
                        <h2>Loading showroom</h2>
                        <p>Pulling live showroom content for the experience.</p>
                    </div>
                </div>
            );
        }

        if (!hasShowroomContent) {
            return (
                <div className="ExperienceStateScreen" style={{ flexDirection: 'column' }}>
                    {sections.length > 1 && (
                        <div className="SectionTabs">
                            {sections.map((section, i) => (
                                <button
                                    key={section.id}
                                    className={`SectionTab ${i === currentSectionIndex ? 'active' : ''}`}
                                    onClick={() => switchSection(i)}
                                >
                                    {section.name}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="ExperienceStateCard">
                        <h2>Showroom unavailable</h2>
                        <p>{sectionsError || 'No production showroom content is published yet.'}</p>
                        <button className="ExperienceStateBtn" onClick={() => navigate('/', { replace: true })}>
                            Go to home
                        </button>
                    </div>
                </div>
            );
        }


        // if(!account?.admin) return;

        return (
            <div className="VideoBannerContent">

                <div className={`VideoBannerSkipper${isAtTargetTimestamp ? '' : ' skipper--travelling'}`}>

                    {isAtTargetTimestamp && (
                        <div className="VideoBannerSkipperTitleRow">
                            <div>
                                <div className="VideoBannerSkipperTitle">Navigate through the showroom</div>
                                <div className="VideoBannerSkipperText">Use the arrows to navigate through each section.</div>
                            </div>
                            <button
                                className={`AutoAdvanceToggle${autoAdvanceEnabled ? ' on' : ' off'}`}
                                onClick={() => setAutoAdvanceEnabled(v => !v)}
                                data-tooltip-id="experience-tooltip"
                                data-tooltip-place="top"
                                data-tooltip-content={autoAdvanceEnabled ? 'Disable autoplay' : 'Enable autoplay'}
                            >
                                {autoAdvanceEnabled ? <RiTimerLine size={15} /> : <RiTimerFlashLine size={15} />}
                            </button>
                        </div>
                    )}

                    <div className="ChapterSteps">
                        {VIDEO_DATA.timestamps.map((_, i) => {
                            const pills = [];
                            const isChapterActive = isAtTargetTimestamp && i === currentTimestampIndex;
                            const isChapterVisited = i < currentTimestampIndex;
                            pills.push(
                                <button
                                    key={`ch-${i}`}
                                    className={`ChapterStep${isChapterActive ? ' active' : ''}${isChapterVisited ? ' visited' : ''}`}
                                    onClick={() => jumpToTimestamp(i)}
                                    title={CHAPTER_LABELS[i] || `Room ${i + 1}`}
                                >
                                    <span className="ChapterStepNum">{i + 1}</span>
                                </button>
                            );
                            if (i < VIDEO_DATA.timestamps.length - 1) {
                                const isTravelActive = !isAtTargetTimestamp && travelSegmentIndex === i;
                                const isTravelVisited = isAtTargetTimestamp
                                    ? i < currentTimestampIndex
                                    : travelSegmentIndex !== null && i < travelSegmentIndex;
                                pills.push(
                                    <div
                                        key={`tr-${i}`}
                                        className={`ChapterStep ChapterStep--travel${isTravelActive ? ' active' : ''}${isTravelVisited ? ' visited' : ''}`}
                                    />
                                );
                            }
                            return pills;
                        })}
                    </div>

                    {!isAtTargetTimestamp && travelSegmentIndex !== null && (
                        <div className="TravelProgressBarWrapper">
                            <div className="TravelProgressBar" ref={progressBarRef} style={{ width: '0%' }} />
                        </div>
                    )}

                    {isAtTargetTimestamp && (
                    <div className="VideoBannerSkipperOptions">
                        <button 
                            className="VideoBannerSkipperOptionsArrow Left" 
                            onClick={prevTimestamp}
                            disabled={currentTimestampIndex === 0}
                            style={{
                                opacity: currentTimestampIndex === 0 ? 0.3 : 1
                            }}
                        >
                            <RiArrowLeftCircleFill  />
                            <span className="VideoBannerSkipperOptionsArrowText">Previous</span>
                        </button>

                        {(() => {
                            const isLastChapter = currentTimestampIndex === VIDEO_DATA.timestamps.length - 1;
                            const isLastSection = currentSectionIndex === sections.length - 1;
                            const videoPlaying = videoRef.current && !videoRef.current.paused;

                            if (isAtTargetTimestamp && isLastChapter && isLastSection) {
                                // End of everything — show Close CTA
                                return (
                                    <button
                                        className="VideoBannerSkipperOptionsArrow Right EndCTA"
                                        onClick={() => navigate(-1)}
                                    >
                                        <span className="VideoBannerSkipperOptionsArrowText">Close</span>
                                        <RiCloseLine />
                                    </button>
                                );
                            }
                            if (isAtTargetTimestamp && isLastChapter && !isLastSection) {
                                // End of this section — show Next Section
                                return (
                                    <button
                                        className="VideoBannerSkipperOptionsArrow Right NextSection"
                                        onClick={() => switchSection(currentSectionIndex + 1)}
                                    >
                                        <span className="VideoBannerSkipperOptionsArrowText">Next Section</span>
                                        <RiArrowRightCircleFill />
                                    </button>
                                );
                            }
                            if (!isLastChapter || videoPlaying) {
                                return (
                                    <button
                                        className="VideoBannerSkipperOptionsArrow Right"
                                        onClick={nextTimestamp}
                                    >
                                        <span className="VideoBannerSkipperOptionsArrowText">
                                            {videoPlaying ? 'Skip' : 'Next'}
                                        </span>
                                        <RiArrowRightCircleFill />
                                    </button>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    )}

                    {autoAdvanceEnabled && isAtTargetTimestamp && videoInitialized && !selectedDot && currentTimestampIndex < VIDEO_DATA.timestamps.length - 1 && (
                        <div className="AutoAdvanceBarWrapper">
                            <div
                                className="AutoAdvanceBar"
                                key={`${currentTimestampIndex}-${isAtTargetTimestamp}`}
                            />
                        </div>
                    )}
                </div>

                {/* ── Shop this scene strip ── */}
                {isAtTargetTimestamp && (() => {
                    const dots = VIDEO_DATA.timestamps[currentTimestampIndex]?.dots;
                    if (!dots?.length) return null;
                    return (
                        <div className={`SceneShopStrip ${experienceCartItems.length > 0 ? 'Cart' : ''}`}>
                            <span className="SceneShopLabel">In this scene</span>
                            <div className="SceneShopItems">
                                {dots.map((dot, i) => (
                                    <button
                                        key={dot.item?.productId ?? i}
                                        className="SceneShopItem"
                                        onClick={() => openProductDrawer(dot.item?.productId)}
                                    >
                                        {dot.item?.image
                                            ? <img src={dot.item.image} alt={dot.item.title} onError={e => e.target.style.display='none'} />
                                            : <div className="SceneShopItemImagePlaceholder" />
                                        }
                                        <span className="SceneShopItemName">{dot.item?.title}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                <button
                    className={`BannerArrow Left${currentTimestampIndex === 0 ? ' invisible' : ''}`}
                    onClick={prevTimestamp}
                    disabled={currentTimestampIndex === 0}
                >
                    <RiArrowLeftCircleFill  />
                </button>

                <div className="SectionTabs">
                    {sections.map((section, i) => (
                        <button
                            key={section.id}
                            className={`SectionTab ${i === currentSectionIndex ? 'active' : ''}`}
                            onClick={() => switchSection(i)}
                        >
                            {section.name}
                        </button>
                    ))}
                </div>

                <div className="VideoBanner">
                    {renderVideoBanner()}
                </div>

                <button
                    className={`BannerArrow Right${
                        currentTimestampIndex < VIDEO_DATA.timestamps.length - 1 ||
                        (currentTimestampIndex === VIDEO_DATA.timestamps.length - 1 && videoRef.current && !videoRef.current.paused)
                            ? '' : ' invisible'
                    }`}
                    onClick={nextTimestamp}
                    disabled={
                        !(currentTimestampIndex < VIDEO_DATA.timestamps.length - 1 ||
                        (currentTimestampIndex === VIDEO_DATA.timestamps.length - 1 && videoRef.current && !videoRef.current.paused))
                    }
                >
                    <RiArrowRightCircleFill  />
                </button>
            </div>
        );
    };

    return (
        <>
        <div className="Dash">
            <button className="ExperienceExitBtn" onClick={() => navigate(-1)} title="Go back">
                <RiArrowLeftLine size={15} />
                Back
            </button>
           { getMainView() }

            {/* ── Product Drawer ─────────────────────────────────────────────── */}
            {drawerOpen && (
                <div className="ProductDrawerBackdrop" onClick={closeProductDrawer} />
            )}
            <div className={`ProductDrawerPanel${drawerOpen ? ' open' : ''}`}>
                <button className="ProductDrawerClose" onClick={closeProductDrawer} title="Close">
                    <RiCloseLine size={22} />
                </button>

                {drawerLoading && (
                    <div className="ProductDrawerLoading">
                        <span>Loading product…</span>
                    </div>
                )}

                {!drawerLoading && !drawerProduct && drawerOpen && (
                    <div className="ProductDrawerLoading">
                        <span>Product not found.</span>
                    </div>
                )}

                {!drawerLoading && drawerProduct && (() => {
                    const tp = getDrawerTransformed();
                    return (
                        <div className="ProductDrawerContent">
                            {/* Images */}
                            <div className="ProductDrawerImages">
                                <div className="ProductDrawerMainImage">
                                    <img
                                        src={tp.images[drawerImageIndex]?.url || undefined}
                                        alt={tp.images[drawerImageIndex]?.alt || tp.name}
                                        onError={e => e.target.removeAttribute('src')}
                                    />
                                    {tp.images.length > 1 && (
                                        <div className="ProductDrawerImageDots">
                                            {tp.images.map((_, i) => (
                                                <button
                                                    key={i}
                                                    className={`ProductDrawerImageDot${i === drawerImageIndex ? ' active' : ''}`}
                                                    onClick={() => setDrawerImageIndex(i)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {tp.images.length > 1 && (
                                    <div className="ProductDrawerThumbs">
                                        {tp.images.map((img, i) => (
                                            <button
                                                key={img.id}
                                                className={`ProductDrawerThumb${i === drawerImageIndex ? ' active' : ''}`}
                                                onClick={() => setDrawerImageIndex(i)}
                                            >
                                                <img src={img.url || undefined} alt={img.alt} onError={e => e.target.removeAttribute('src')} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Details */}
                            <div className="ProductDrawerDetails">
                                {tp.brand && <div className="ProductDrawerBrand">{tp.brand}</div>}
                                <h2 className="ProductDrawerTitle">{tp.name}</h2>
                                <p className="ProductDrawerShortDesc">{tp.shortDescription}</p>

                                {/* Pricing */}
                                <div className="ProductDrawerPricing">
                                    {tp.comingSoon ? (
                                        <span className="ProductDrawerComingSoon">Coming Soon</span>
                                    ) : (
                                        <>
                                            <span className="ProductDrawerPrice">
                                                {tp.price.curr} {calculateDrawerTotal(tp).toFixed(2)}
                                            </span>
                                            {tp.price.originalAmount && (
                                                <span className="ProductDrawerOriginalPrice">
                                                    {tp.price.curr}{(tp.price.originalAmount * drawerQuantity).toFixed(2)}
                                                </span>
                                            )}
                                            {tp.price.discount > 0 && (
                                                <span className="ProductDrawerDiscount">{tp.price.discount}% OFF</span>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Stock */}
                                <div className="ProductDrawerStock">
                                    {tp.inStock ? (
                                        <span className="ProductDrawerInStock">
                                            <RiCheckLine size={14} /> In Stock
                                            {tp.stockCount !== 999 && tp.stockCount > 0 ? ` (${tp.stockCount} available)` : null}
                                        </span>
                                    ) : (
                                        <span className="ProductDrawerOutStock">
                                            <RiErrorWarningLine size={14} /> Out of Stock
                                        </span>
                                    )}
                                </div>

                                {/* Modifiers */}
                                {tp.modifiers.length > 0 && (
                                    <div className="ProductDrawerModifiers">
                                        {tp.modifiers.map(mod => (
                                            <div key={mod.id} className="ProductDrawerModGroup">
                                                <label className="ProductDrawerModLabel">
                                                    {mod.name}
                                                    {mod.required && <span style={{ color: '#dc2626' }}> *</span>}
                                                </label>
                                                {mod.id === 'color' ? (
                                                    <div className="ProductDrawerColorOptions">
                                                        {mod.options.map(opt => (
                                                            <button
                                                                key={opt.id}
                                                                className={`ProductDrawerColorOpt${drawerModifiers[mod.id]?.id === opt.id ? ' selected' : ''}${!opt.inStock ? ' out-of-stock' : ''}`}
                                                                onClick={() => opt.inStock && setDrawerModifiers(prev => ({ ...prev, [mod.id]: opt }))}
                                                                disabled={!opt.inStock}
                                                            >
                                                                <div className="color-swatch" style={{ backgroundColor: opt.color }} />
                                                                <span>{opt.name}{opt.amount > 0 ? ` — $${opt.amount}` : ''}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <select
                                                        className="ProductDrawerSelect"
                                                        value={drawerModifiers[mod.id]?.id || ''}
                                                        onChange={e => {
                                                            const opt = mod.options.find(o => o.id === e.target.value);
                                                            if (opt) setDrawerModifiers(prev => ({ ...prev, [mod.id]: opt }));
                                                        }}
                                                    >
                                                        <option value="">Select {mod.name}</option>
                                                        {mod.options.map(opt => (
                                                            <option key={opt.id} value={opt.id}>
                                                                {opt.name}
                                                                {opt.description ? ` - ${opt.description}` : ''}
                                                                {opt.amount > 0 ? ` — $${opt.amount}` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Quantity */}
                                <div className="ProductDrawerQuantity">
                                    <span className="ProductDrawerQuantityLabel">Quantity</span>
                                    <div className="ProductDrawerQuantityControls">
                                        <button
                                            onClick={() => setDrawerQuantity(q => Math.max(1, q - 1))}
                                            disabled={drawerQuantity <= 1}
                                        >
                                            <RiSubtractLine />
                                        </button>
                                        <span className="ProductDrawerQuantityValue">{drawerQuantity}</span>
                                        <button
                                            onClick={() => setDrawerQuantity(q => Math.min(tp.stockCount || 99, q + 1))}
                                            disabled={tp.stockCount !== 999 && drawerQuantity >= tp.stockCount}
                                        >
                                            <RiAddLine />
                                        </button>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="ProductDrawerActions">
                                    {drawerAddedToCart ? (
                                        <div className="ProductDrawerPostAddActions">
                                            <button
                                                className="ProductDrawerSecondaryAction"
                                                onClick={openExperienceCart}
                                            >
                                                View cart
                                            </button>
                                            <button
                                                className="ProductDrawerAddToCart checkout"
                                                onClick={() => navigate(account ? '/checkout' : '/sign-in?ref=cart')}
                                            >
                                                Checkout
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="ProductDrawerAddToCart"
                                            onClick={() => handleDrawerAddToCart(tp)}
                                            disabled={tp.comingSoon || !tp.inStock || drawerAddingToCart}
                                        >
                                            <RiShoppingCartLine size={16} />
                                            {tp.comingSoon ? 'Coming Soon' :
                                             drawerAddingToCart ? 'Adding…' :
                                             `Add to Cart - ${tp.price.curr} ${calculateDrawerTotal(tp).toFixed(2)}`}
                                        </button>
                                    )}
                                    <button
                                        className={`ProductDrawerWishlist${drawerWishlisted ? ' active' : ''}`}
                                        onClick={() => handleDrawerWishlistToggle(tp)}
                                        disabled={drawerTogglingWishlist}
                                        title={drawerWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                                    >
                                        {drawerWishlisted ? <RiHeart3Fill size={18} /> : <RiHeart3Line size={18} />}
                                    </button>
                                </div>

                                {/* Description */}
                                {tp.description && (
                                    <div className="ProductDrawerDescription">
                                        <h4>Description</h4>
                                        <p>{tp.description}</p>
                                    </div>
                                )}

                                {/* Features */}
                                {tp.features.length > 0 && (
                                    <div className="ProductDrawerFeatures">
                                        <h4>Key Features</h4>
                                        <ul>
                                            {tp.features.map((f, i) => <li key={i}>{f}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </div>

            {experienceCartItems.length > 0 && (
                <>
                    {experienceCartOpen && <div className="ExperienceCartBackdrop" onClick={() => setExperienceCartOpen(false)} />}

                    <div className={`ExperienceCartPanel${experienceCartOpen ? ' open' : ''}`}>
                        <div className="ExperienceCartPanelHeader">
                            <div>
                                <h4>Your cart</h4>
                                <span>{experienceCartItems.length} item{experienceCartItems.length === 1 ? '' : 's'}</span>
                            </div>
                            <button className="ExperienceCartPanelClose" onClick={() => setExperienceCartOpen(false)} title="Close cart">
                                <RiCloseLine size={18} />
                            </button>
                        </div>

                        {experienceCartLoading ? (
                            <div className="ExperienceDrawerCartLoading">
                                <RiLoader4Line className="ExperienceDrawerSpin" size={18} />
                                <span>Loading cart…</span>
                            </div>
                        ) : (
                            <>
                                <div className="ExperienceDrawerCartItems">
                                    {experienceCartItems.map((item) => {
                                        const itemBusy = Boolean(experienceCartUpdating[item.cart_id]);
                                        return (
                                            <div className="ExperienceDrawerCartItem" key={item.cart_id}>
                                                {item.image && <img className="ExperienceDrawerCartItemImage" src={item.image} alt={item.name} onError={e => e.target.style.display = 'none'} />}
                                                <div className="ExperienceDrawerCartItemBody">
                                                    <div className="ExperienceDrawerCartItemTop">
                                                        <div>
                                                            <div className="ExperienceDrawerCartItemName">{item.name}</div>
                                                            <div className="ExperienceDrawerCartItemPrice">
                                                                {item.price_curr} {getDrawerCartItemTotal(item).toFixed(2)}
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="ExperienceDrawerCartRemove"
                                                            onClick={() => removeDrawerCartItem(item.cart_id)}
                                                            disabled={itemBusy}
                                                            title="Remove item"
                                                        >
                                                            <RiDeleteBin6Line size={16} />
                                                        </button>
                                                    </div>

                                                    <div className="ExperienceDrawerCartQty">
                                                        <button
                                                            onClick={() => updateDrawerCartQuantity(item.cart_id, (parseInt(item.qty, 10) || 1) - 1)}
                                                            disabled={itemBusy || parseInt(item.qty, 10) <= 1}
                                                        >
                                                            <RiSubtractLine size={14} />
                                                        </button>
                                                        <span>{item.qty}</span>
                                                        <button
                                                            onClick={() => updateDrawerCartQuantity(item.cart_id, (parseInt(item.qty, 10) || 0) + 1)}
                                                            disabled={itemBusy}
                                                        >
                                                            <RiAddLine size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="ExperienceDrawerCartSummary">
                                    <div>
                                        <span>Total</span>
                                        <strong>{getDrawerCartCurrency()} {getDrawerCartTotal().toFixed(2)}</strong>
                                    </div>
                                    <button
                                        className="ExperienceDrawerCheckoutBtn full"
                                        onClick={() => navigate(account ? '/checkout' : '/sign-in?ref=cart')}
                                        disabled={experienceCartItems.length === 0}
                                    >
                                        Checkout
                                        <RiArrowRightLine size={16} />
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <button
                        className={`ExperienceFloatingCart${experienceCartOpen ? ' open' : ''}`}
                        onClick={toggleExperienceCart}
                    >
                        <RiShoppingCartLine size={18} />
                        <span>View cart</span>
                        <span className="ExperienceFloatingCartCount">{experienceCartItems.length}</span>
                    </button>
                </>
            )}

        </div>
        <Tooltip id="experience-tooltip" />
        </>
    );
}
export default Experience;
